import {useMemo} from 'react';
import {
  createDirectus,
  staticToken,
  rest,
  readItems,
  createItem,
  updateItem,
  deleteItem,
  uploadFiles,
  readFile,
  deleteFile,
  updateFile
} from '@directus/sdk';
import {
  DocumentRecord,
  DocumentContent,
  DocumentApiConfig
} from '../types/document';

// 数据转换函数
const transformToDocumentRecord = (raw: any): DocumentRecord => {
  return {
    id: raw.id,
    name: raw.name || '未命名文档',
    fileName: raw.fileName,
    date_created: raw.date_created,
    date_updated: raw.date_updated
  };
};

const transformToDocumentRecords = (rawList: any[]): DocumentRecord[] => {
  return Array.isArray(rawList) ? rawList.map(transformToDocumentRecord) : [];
};

export function useDocumentAPI(config: DocumentApiConfig) {
  const {apiUrl, accessToken} = config;

  const directus = useMemo(() => {
    if (!apiUrl || !accessToken) return null;
    return createDirectus(apiUrl).with(staticToken(accessToken)).with(rest());
  }, [apiUrl, accessToken]);

  const documentAPI = useMemo(() => {
    if (!directus) return null;

    return {
      // 文档记录操作
      getDocuments: async (): Promise<DocumentRecord[]> => {
        try {
          console.log('[DocumentAPI] 获取文档列表...');
          const response = await directus.request(readItems('pad_records',{ sort: ['date_created'], limit: -1 }));
          const result = transformToDocumentRecords(response);
          console.log('[DocumentAPI] 文档列表获取成功:', result);
          return result;
        } catch (error) {
          console.error('[DocumentAPI] 获取文档列表失败:', error);
          return [];
        }
      },

      createDocument: async (
        payload: Omit<DocumentRecord, 'id'>
      ): Promise<DocumentRecord> => {
        try {
          console.log('[DocumentAPI] 创建文档记录:', payload);
          const response = await directus.request(
            createItem('pad_records', payload)
          );
          const result = transformToDocumentRecord(response);
          console.log('[DocumentAPI] 文档记录创建成功:', result);
          return result;
        } catch (error) {
          console.error('[DocumentAPI] 创建文档失败:', error);
          throw error;
        }
      },

      updateDocument: async (
        id: number,
        payload: Partial<DocumentRecord>
      ): Promise<DocumentRecord> => {
        try {
          console.log('[DocumentAPI] 更新文档记录:', id, payload);
          const response = await directus.request(
            updateItem('pad_records', id, payload)
          );
          const result = transformToDocumentRecord(response);
          console.log('[DocumentAPI] 文档记录更新成功:', result);
          return result;
        } catch (error) {
          console.error('[DocumentAPI] 更新文档失败:', error);
          throw error;
        }
      },

      deleteDocument: async (id: number): Promise<void> => {
        try {
          console.log('[DocumentAPI] 删除文档记录:', id);
          await directus.request(deleteItem('pad_records', id));
          console.log('[DocumentAPI] 文档记录删除成功');
        } catch (error) {
          console.error('[DocumentAPI] 删除文档失败:', error);
          throw error;
        }
      },

      // 文件操作
      getDocumentContent: async (
        fileName: string
      ): Promise<{content: string}> => {
        try {
          const fileUrl = `${apiUrl}/assets/${fileName}`;
          //   const response = await fetch(fileUrl);
          const response = await fetch(fileUrl, {
            headers: {Authorization: `Bearer ${accessToken}`},
          });

          if (!response.ok) {
            throw new Error(`获取文件内容失败: ${response.status}`);
          }

          //     const schemaText = await response.text();
          //   console.log('[DocumentAPI] 获取文档内容:', fileName);
          //   const response = await directus.request(readFile(fileName));
          console.log('[DocumentAPI] 文档内容获取成功:', response);

          const content = await response.text();

          return {content};
        } catch (error) {
          console.error('[DocumentAPI] 读取文档内容失败:', error);
          return {content: ''};
        }
      },

      createDocumentFile: async (
        content: string,
        name: string
      ): Promise<{id: string}> => {
        try {
          console.log('[DocumentAPI] 创建文档文件:', name);
          const blob = new Blob([content], {type: 'text/plain'});
          const file = new File([blob], `${name}.txt`, {type: 'text/plain'});

          const formData = new FormData();
          formData.append('file', file);
          formData.append('title', name);

          const response = await directus.request(uploadFiles(formData));
          console.log('[DocumentAPI] 文档文件创建成功:', response);

          // 处理响应格式
          const fileId = response?.id || response?.[0]?.id;
          if (!fileId) {
            throw new Error('文件创建失败：未返回文件ID');
          }

          return {id: fileId};
        } catch (error) {
          console.error('[DocumentAPI] 创建文档文件失败:', error);
          throw error;
        }
      },

      updateDocumentFile: async (
        fileName: string,
        content: string,
        name: string
      ): Promise<void> => {
        try {
          console.log('[DocumentAPI] 更新文档文件:', fileName, name);
          const blob = new Blob([content], {type: 'text/plain'});
          const file = new File([blob], `${name}.txt`, {type: 'text/plain'});

          const formData = new FormData();
          formData.append('file', file);
          formData.append('title', name);

          await directus.request(updateFile(fileName, formData));
          console.log('[DocumentAPI] 文档文件更新成功');
        } catch (error) {
          console.error('[DocumentAPI] 更新文档文件失败:', error);
          throw error;
        }
      },

      deleteDocumentFile: async (fileName: string): Promise<void> => {
        try {
          console.log('[DocumentAPI] 删除文档文件:', fileName);
          await directus.request(deleteFile(fileName));
          console.log('[DocumentAPI] 文档文件删除成功');
        } catch (error) {
          console.error('[DocumentAPI] 删除文档文件失败:', error);
          throw error;
        }
      }
    };
  }, [directus]);

  return documentAPI;
}
